import { useEffect, useMemo, useState } from 'react';
import {
  api, type ComedyAccount, type ComedyAvailableAccount, type ComedyChannel,
} from '../../lib/api';
import { Button, ErrorAlert, Spinner } from '../../components/ui';
import {
  COMEDY_PLATFORM_LABEL, ComedyEmpty, ComedySection, ComedyStatus, Field, splitList,
} from './ComedyShared';

const LANGUAGES = [{ value: 'pl', label: 'Polski' }, { value: 'en', label: 'English' }];
const CAPTION_STYLES = [
  ['DYNAMIC_POP', 'Dynamic Pop Captions'], ['KARAOKE_HIGHLIGHT', 'Karaoke Highlight'],
  ['BOLD_COMEDY', 'Bold Comedy Captions'], ['WORD_BY_WORD', 'Word-by-Word'],
  ['MINIMAL_CLEAN', 'Minimal Clean'], ['MEME_CAPTIONS', 'Meme Captions'],
] as const;
const MODES = [['MANUAL', 'MANUAL'], ['SEMI_AUTO', 'SEMI-AUTO (domyślny)'], ['AUTOPILOT', 'AUTOPILOT']] as const;
const CREATION_MODES = [['HYBRID_AUTO', 'HYBRID AUTO (domyślny)'], ['STORY_FIRST', 'STORY FIRST'], ['FOOTAGE_FIRST', 'FOOTAGE FIRST']] as const;

export function channelPayload(form: Record<string, unknown>) {
  return {
    name: form.name,
    slug: form.slug || undefined,
    description: form.description || null,
    language: form.language,
    country: form.country,
    topics: splitList(String(form.topics ?? '')),
    humorCategories: splitList(String(form.humorCategories ?? '')),
    keywords: splitList(String(form.keywords ?? '')),
    excludedTopics: splitList(String(form.excludedTopics ?? '')),
    humorStyle: form.humorStyle,
    audience: form.audience || null,
    durationMinSeconds: Number(form.durationMinSeconds),
    durationMaxSeconds: Number(form.durationMaxSeconds),
    postsPerDay: Number(form.postsPerDay),
    publishTimes: splitList(String(form.publishTimes ?? '')).filter((time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time)),
    timezone: form.timezone || 'Europe/Warsaw',
    automationMode: form.automationMode,
    creationMode: form.creationMode,
    captionStyle: form.captionStyle,
    aiBudgetDailyUsd: Number(form.aiBudgetDailyUsd),
    maxCostPerVideoUsd: Number(form.maxCostPerVideoUsd),
    dailyRenderLimit: Number(form.dailyRenderLimit),
    researchIntervalMinutes: Number(form.researchIntervalMinutes),
    requireManualApproval: Boolean(form.requireManualApproval),
    requireCommercialRights: Boolean(form.requireCommercialRights),
    requireModificationRights: Boolean(form.requireModificationRights),
    scheduleEnabled: Boolean(form.scheduleEnabled),
    status: form.status,
    editSettings: {
      captionsEnabled: form.captionsEnabled !== false,
      captionFontSize: Number(form.captionFontSize ?? 64),
      safeArea: form.safeArea ?? 'YOUTUBE_SHORTS',
      effectsEnabled: form.effectsEnabled !== false,
      maxEffects: Number(form.maxEffects ?? 4),
      transition: form.transition ?? 'QUICK_ZOOM',
      backgroundMusic: form.backgroundMusic ?? 'NONE',
      titleCard: Boolean(form.titleCard),
      burnedInDisclosure: form.burnedInDisclosure !== false,
      accentColor: form.accentColor ?? '#FFD84D',
      clipPaddingMs: Number(form.clipPaddingMs ?? 120),
      mixMode: form.mixMode === 'SENTENCE_MIX' ? 'SENTENCE_MIX' : 'SEGMENT_CUT',
    },
  };
}

function defaultForm(): Record<string, unknown> {
  return {
    name: '', slug: '', description: '', language: 'pl', country: 'PL', topics: '', humorCategories: '',
    keywords: '', excludedTopics: '', humorStyle: 'sytuacyjny', audience: '', durationMinSeconds: 20,
    durationMaxSeconds: 45, postsPerDay: 2, publishTimes: '12:00, 18:00', timezone: 'Europe/Warsaw',
    automationMode: 'SEMI_AUTO', creationMode: 'HYBRID_AUTO', captionStyle: 'DYNAMIC_POP',
    aiBudgetDailyUsd: 1, maxCostPerVideoUsd: 0.5, dailyRenderLimit: 2, researchIntervalMinutes: 1440,
    requireManualApproval: true, requireCommercialRights: true, requireModificationRights: true,
    scheduleEnabled: true, status: 'ACTIVE', captionsEnabled: true, captionFontSize: 64,
    safeArea: 'YOUTUBE_SHORTS', effectsEnabled: true, maxEffects: 4, transition: 'QUICK_ZOOM',
    backgroundMusic: 'NONE', titleCard: false, burnedInDisclosure: true, accentColor: '#FFD84D', clipPaddingMs: 120, mixMode: 'SEGMENT_CUT',
  };
}

function channelToForm(channel: ComedyChannel): Record<string, unknown> {
  return {
    ...defaultForm(), ...channel,
    topics: channel.topics.join(', '), humorCategories: channel.humorCategories.join(', '),
    keywords: channel.keywords.join(', '), excludedTopics: channel.excludedTopics.join(', '),
    publishTimes: channel.publishTimes.join(', '), slug: channel.slug,
    ...(channel.editSettings ?? {}),
  };
}

export function ComedyChannelsPanel({ channels, selectedId, onSelect, onRefresh }: { channels: ComedyChannel[]; selectedId: string | null; onSelect: (id: string) => void; onRefresh: () => void }) {
  const [editing, setEditing] = useState<ComedyChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>(defaultForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const startCreate = () => { setEditing(null); setCreating(true); setForm(defaultForm()); setError(null); setMessage(''); };
  const startEdit = (channel: ComedyChannel) => { setEditing(channel); setCreating(false); setForm(channelToForm(channel)); setError(null); setMessage(''); };
  const update = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy(true); setError(null); setMessage('');
    try {
      const payload = channelPayload(form);
      const saved = editing
        ? await api.comedy.updateChannel(editing.id, payload)
        : await api.comedy.createChannel(payload);
      setMessage(editing ? 'Kanał zapisany.' : 'Kanał utworzony. Dodano domyślne źródła researchu.');
      setCreating(false); setEditing(saved); setForm(channelToForm(saved));
      onRefresh(); onSelect(saved.id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const clone = async (channel: ComedyChannel) => {
    if (!window.confirm(`Sklonować konfigurację kanału „${channel.name}”? Profile social media nie zostaną skopiowane.`)) return;
    setBusy(true); setError(null);
    try {
      const cloned = await api.comedy.cloneChannel(channel.id, { name: `${channel.name} (kopia)`, copySources: true });
      setMessage('Kanał sklonowany (status PAUSED). Przypisz profile i włącz go, gdy będzie gotowy.');
      onRefresh(); onSelect(cloned.id); setEditing(cloned); setForm(channelToForm(cloned));
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const archive = async (channel: ComedyChannel) => {
    if (!window.confirm(`Zarchiwizować kanał „${channel.name}”? Dane pozostaną w bazie, ale produkcja i publikacja zostaną zatrzymane.`)) return;
    setBusy(true);
    try { await api.comedy.archiveChannel(channel.id); onRefresh(); setEditing(null); setCreating(false); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="Kanały tematyczne"
        description="Nielimitowana liczba niezależnych kanałów. Każdy ma własne źródła, bibliotekę, prawa, harmonogram i przypisane profile."
        actions={<Button variant="primary" size="sm" onClick={startCreate}>+ Nowy kanał</Button>}
      >
        {channels.length === 0 ? (
          <ComedyEmpty title="Brak kanałów" hint="Utwórz pierwszy kanał, np. „Absurdalne Rozmowy” (PL) lub „Celebrity Chaos” (EN)." action={<Button variant="primary" onClick={startCreate}>Utwórz kanał</Button>} />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Kanał</th><th>Język</th><th>Tryb</th><th>Profile</th><th>Scenariusze</th><th>Status</th><th /></tr></thead>
              <tbody>
                {channels.map((channel) => (
                  <tr key={channel.id} onClick={() => onSelect(channel.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{channel.name}</strong><div className="muted" style={{ fontSize: 11 }}>{channel.topics.slice(0, 3).join(' · ') || channel.humorStyle}</div></td>
                    <td>{channel.language.toUpperCase()}</td>
                    <td>{channel.automationMode}<div className="muted" style={{ fontSize: 11 }}>{channel.creationMode}</div></td>
                    <td>{channel.accountCount ?? 0}</td>
                    <td>{channel.pendingScenarios ?? 0}</td>
                    <td><ComedyStatus value={channel.status} /></td>
                    <td>
                      <div className="btn-row">
                        <Button size="sm" onClick={(event) => { event.stopPropagation(); startEdit(channel); }}>Edytuj</Button>
                        <Button size="sm" onClick={(event) => { event.stopPropagation(); void clone(channel); }}>Klonuj</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>

      {(creating || editing) && (
        <ComedySection
          title={creating ? 'Nowy kanał tematyczny' : `Edycja: ${editing?.name}`}
          description="Konfiguracja jest izolowana per kanał. Zmiany praw i trybu AUTOPILOT wymagają świadomego potwierdzenia."
          actions={<div className="btn-row"><Button size="sm" onClick={() => { setCreating(false); setEditing(null); }}>Zamknij</Button><Button variant="primary" size="sm" onClick={() => void save()} disabled={busy}>{busy ? 'Zapisywanie…' : 'Zapisz kanał'}</Button></div>}
        >
          {error ? <ErrorAlert error={error} /> : null}
          {message ? <div className="alert alert--success">{message}</div> : null}
          <div className="form-row">
            <Field label="Nazwa"><input value={String(form.name ?? '')} onChange={(event) => update('name', event.target.value)} placeholder="np. Absurdalne Rozmowy" /></Field>
            <Field label="Slug (opcjonalny)" hint="Stały identyfikator URL; generowany z nazwy, jeśli pusty."><input value={String(form.slug ?? '')} onChange={(event) => update('slug', event.target.value)} placeholder="absurdalne-rozmowy" /></Field>
          </div>
          <Field label="Opis"><textarea rows={2} value={String(form.description ?? '')} onChange={(event) => update('description', event.target.value)} placeholder="Co jest zabawne w tym kanale?" /></Field>
          <div className="form-row">
            <Field label="Język"><select value={String(form.language)} onChange={(event) => update('language', event.target.value)}>{LANGUAGES.map((language) => <option key={language.value} value={language.value}>{language.label}</option>)}</select></Field>
            <Field label="Kraj docelowy"><input value={String(form.country ?? '')} onChange={(event) => update('country', event.target.value.toUpperCase().slice(0, 3))} /></Field>
          </div>
          <div className="form-row">
            <Field label="Tematyka" hint="Oddziel przecinkami."><input value={String(form.topics ?? '')} onChange={(event) => update('topics', event.target.value)} placeholder="celebryci, wywiady, talk-show" /></Field>
            <Field label="Kategorie humoru" hint="np. absurd, sytuacyjny, biurowy"><input value={String(form.humorCategories ?? '')} onChange={(event) => update('humorCategories', event.target.value)} /></Field>
          </div>
          <div className="form-row">
            <Field label="Słowa kluczowe researchu"><input value={String(form.keywords ?? '')} onChange={(event) => update('keywords', event.target.value)} /></Field>
            <Field label="Wykluczone tematy"><input value={String(form.excludedTopics ?? '')} onChange={(event) => update('excludedTopics', event.target.value)} placeholder="polityka, tragedie" /></Field>
          </div>
          <div className="form-row">
            <Field label="Styl humoru"><input value={String(form.humorStyle ?? '')} onChange={(event) => update('humorStyle', event.target.value)} /></Field>
            <Field label="Grupa odbiorców"><input value={String(form.audience ?? '')} onChange={(event) => update('audience', event.target.value)} /></Field>
          </div>
          <div className="form-row">
            <Field label="Min. długość (s)"><input type="number" min={5} max={180} value={Number(form.durationMinSeconds)} onChange={(event) => update('durationMinSeconds', Number(event.target.value))} /></Field>
            <Field label="Maks. długość (s)"><input type="number" min={5} max={180} value={Number(form.durationMaxSeconds)} onChange={(event) => update('durationMaxSeconds', Number(event.target.value))} /></Field>
          </div>
          <div className="form-row">
            <Field label="Filmów dziennie"><input type="number" min={0} max={20} value={Number(form.postsPerDay)} onChange={(event) => update('postsPerDay', Number(event.target.value))} /></Field>
            <Field label="Godziny publikacji" hint="Europe/Warsaw, oddziel przecinkami."><input value={String(form.publishTimes ?? '')} onChange={(event) => update('publishTimes', event.target.value)} placeholder="12:00, 18:00" /></Field>
          </div>
          <div className="form-row">
            <Field label="Strefa czasowa"><input value={String(form.timezone ?? 'Europe/Warsaw')} onChange={(event) => update('timezone', event.target.value)} /></Field>
            <Field label="Styl napisów"><select value={String(form.captionStyle)} onChange={(event) => update('captionStyle', event.target.value)}>{CAPTION_STYLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
          </div>
          <div className="form-row">
            <Field label="Tryb automatyzacji"><select value={String(form.automationMode)} onChange={(event) => update('automationMode', event.target.value)}>{MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
            <Field label="Tryb tworzenia scenariuszy"><select value={String(form.creationMode)} onChange={(event) => update('creationMode', event.target.value)}>{CREATION_MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
          </div>
          <div className="form-row">
            <Field label="Status kanału" hint="PAUSED wstrzymuje automatyczny research/produkcję; ręczne akcje nadal działają.">
              <select value={String(form.status ?? 'PAUSED')} onChange={(event) => update('status', event.target.value)}>
                <option value="ACTIVE">ACTIVE — kanał działa</option>
                <option value="PAUSED">PAUSED — wstrzymany</option>
              </select>
            </Field>
            <Field label="Harmonogram aktywny"><label className="inline" style={{ paddingTop: 8 }}><input type="checkbox" checked={Boolean(form.scheduleEnabled)} onChange={(event) => update('scheduleEnabled', event.target.checked)} /> Publikuj zgodnie z godzinami</label></Field>
          </div>
          <div className="form-row">
            <Field label="Budżet AI / dzień (USD)"><input type="number" min={0} step="0.1" value={Number(form.aiBudgetDailyUsd)} onChange={(event) => update('aiBudgetDailyUsd', Number(event.target.value))} /></Field>
            <Field label="Limit renderów / dzień"><input type="number" min={0} max={50} value={Number(form.dailyRenderLimit)} onChange={(event) => update('dailyRenderLimit', Number(event.target.value))} /></Field>
          </div>
          <div className="form-row">
            <Field label="Maks. koszt filmu (USD)"><input type="number" min={0} step="0.05" value={Number(form.maxCostPerVideoUsd)} onChange={(event) => update('maxCostPerVideoUsd', Number(event.target.value))} /></Field>
            <Field label="Research co ile minut" hint="0 = tylko ręcznie"><input type="number" min={0} max={10080} value={Number(form.researchIntervalMinutes)} onChange={(event) => update('researchIntervalMinutes', Number(event.target.value))} /></Field>
          </div>
          <div className="grid grid--3">
            <label className="inline"><input type="checkbox" checked={Boolean(form.requireManualApproval)} onChange={(event) => update('requireManualApproval', event.target.checked)} /> Ręczne zatwierdzanie filmów</label>
            <label className="inline"><input type="checkbox" checked={Boolean(form.requireCommercialRights)} onChange={(event) => update('requireCommercialRights', event.target.checked)} /> Wymagaj praw komercyjnych</label>
            <label className="inline"><input type="checkbox" checked={Boolean(form.requireModificationRights)} onChange={(event) => update('requireModificationRights', event.target.checked)} /> Wymagaj zgody na przeróbki</label>
            <label className="inline"><input type="checkbox" checked={Boolean(form.burnedInDisclosure)} onChange={(event) => update('burnedInDisclosure', event.target.checked)} /> Wypalaj oznaczenie parodii</label>
            <label className="inline"><input type="checkbox" checked={Boolean(form.effectsEnabled)} onChange={(event) => update('effectsEnabled', event.target.checked)} /> Efekty komediowe</label>
          </div>
          <div style={{ marginTop: 8 }}>
            <div className="field__label">Podgląd stylu napisów</div>
            <div style={{ position: 'relative', height: 84, borderRadius: 10, overflow: 'hidden', background: 'linear-gradient(135deg,#1f2937,#0b1220)', display: 'grid', placeItems: 'center' }}>
              <div style={{
                maxWidth: '88%', padding: '8px 14px', borderRadius: 12,
                background: form.captionStyle === 'MINIMAL_CLEAN' ? 'rgba(3,7,10,.72)' : 'rgba(4,6,9,.68)',
                color: '#fff', fontWeight: 850, lineHeight: 1.08, textAlign: 'center',
                fontSize: Math.max(18, Math.min(34, Number(form.captionFontSize ?? 64) * 0.42)),
                textTransform: form.captionStyle === 'MEME_CAPTIONS' ? 'uppercase' : 'none',
                textShadow: '0 2px 2px rgba(0,0,0,.95)',
                border: form.captionStyle === 'MINIMAL_CLEAN' ? '1px solid rgba(255,255,255,.18)' : 'none',
              }}>
                <span style={{ color: form.captionStyle === 'KARAOKE_HIGHLIGHT' || form.captionStyle === 'DYNAMIC_POP' ? String(form.accentColor ?? '#FFD84D') : '#fff' }}>To</span>{' '}
                <span>nie ma</span>{' '}
                <span style={{ color: form.captionStyle === 'BOLD_COMEDY' ? String(form.accentColor ?? '#FFD84D') : '#fff' }}>sensu</span>
              </div>
            </div>
            <div className="field__hint">Podgląd poglądowy; finalny render korzysta z istniejącego SmartAnimatedCaptions i bezpiecznych stref platformy.</div>
          </div>
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Montaż i napisy (zaawansowane)</summary>
            <div className="form-row" style={{ marginTop: 12 }}>
              <Field label="Rozmiar napisów (px)"><input type="number" min={28} max={110} value={Number(form.captionFontSize ?? 64)} onChange={(event) => update('captionFontSize', Number(event.target.value))} /></Field>
              <Field label="Bezpieczny obszar platformy">
                <select value={String(form.safeArea ?? 'YOUTUBE_SHORTS')} onChange={(event) => update('safeArea', event.target.value)}>
                  <option value="YOUTUBE_SHORTS">YouTube Shorts</option><option value="FACEBOOK_REELS">Facebook Reels</option>
                  <option value="INSTAGRAM_REELS">Instagram Reels</option><option value="TIKTOK">TikTok</option>
                </select>
              </Field>
            </div>
            <div className="form-row">
              <Field label="Maks. liczba efektów (0–6)"><input type="number" min={0} max={6} value={Number(form.maxEffects ?? 4)} onChange={(event) => update('maxEffects', Number(event.target.value))} /></Field>
              <Field label="Przejście"><select value={String(form.transition ?? 'QUICK_ZOOM')} onChange={(event) => update('transition', event.target.value)}><option value="CUT">Cut</option><option value="QUICK_ZOOM">Quick zoom</option><option value="WHIP">Whip</option><option value="FLASH">Flash</option></select></Field>
            </div>
            <div className="form-row">
              <Field label="Kolor akcentu"><input type="color" value={String(form.accentColor ?? '#FFD84D')} onChange={(event) => update('accentColor', event.target.value)} /></Field>
              <Field label="Oddech przy cięciu (ms)"><input type="number" min={0} max={1200} value={Number(form.clipPaddingMs ?? 120)} onChange={(event) => update('clipPaddingMs', Number(event.target.value))} /></Field>
            </div>
            <Field label="Tryb montażu" hint="SENTENCE MIXER tnie dokładnie na zmierzonych granicach zdań; nie syntetyzuje nowych słów.">
              <select value={String(form.mixMode ?? 'SEGMENT_CUT')} onChange={(event) => update('mixMode', event.target.value)}>
                <option value="SEGMENT_CUT">Segment cut (z oddechem)</option>
                <option value="SENTENCE_MIX">Sentence Mixer (precyzyjne cięcia)</option>
              </select>
            </Field>
          </details>
          {!editing && (
            <div className="alert alert--info" style={{ marginTop: 12 }}>
              Po zapisaniu kanału poniżej pojawi się sekcja „Przypisane profile social media”. Wybierzesz tam istniejące konta YouTube / Facebook / Instagram / TikTok — nie trzeba ich ponownie autoryzować.
            </div>
          )}
          {editing && (
            <div className="btn-row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <Button variant="danger" size="sm" onClick={() => void archive(editing)} disabled={busy}>Archiwizuj kanał</Button>
            </div>
          )}
        </ComedySection>
      )}

      {(() => {
        const accountsChannel = editing ?? channels.find((channel) => channel.id === selectedId) ?? null;
        return accountsChannel ? <ComedyAccountsEditor channel={accountsChannel} /> : null;
      })()}
      {!selectedId && channels.length > 0 && <div className="muted">Wybierz kanał, aby zarządzać jego profilem, źródłami i produkcją.</div>}
    </div>
  );
}

function ComedyAccountsEditor({ channel }: { channel: ComedyChannel }) {
  const [assigned, setAssigned] = useState<ComedyAccount[]>([]);
  const [available, setAvailable] = useState<ComedyAvailableAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [settings, setSettings] = useState({ dailyLimit: 1, publishTimes: '', titlePrefix: '', cta: '', hashtags: '' });
  const [bulkMessage, setBulkMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [assignedResponse, availableResponse] = await Promise.all([
        api.comedy.channelAccounts(channel.id), api.comedy.availableAccounts(channel.id),
      ]);
      setAssigned(assignedResponse.accounts);
      setAvailable(availableResponse.accounts);
    } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const assign = async () => {
    if (!selectedIds.length) return;
    setBusy(true); setError(null); setBulkMessage('');
    try {
      const result = await api.comedy.assignAccounts(channel.id, {
        accountIds: selectedIds,
        settings: {
          enabled: true, dailyLimit: settings.dailyLimit,
          publishTimes: splitList(settings.publishTimes).filter((time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time)),
          titlePrefix: settings.titlePrefix, cta: settings.cta, hashtags: splitList(settings.hashtags),
        },
      });
      const skipped = result.skipped.length
        ? ` Pominięto ${result.skipped.length}: ${result.skipped.map((entry) => `${entry.accountId} — ${entry.reason}`).join('; ')}`
        : '';
      setBulkMessage(`Przypisano ${result.assigned.length} profil(i).${skipped}`);
      setSelectedIds((current) => current.filter((id) => !result.assigned.includes(id)));
      setSettings({ dailyLimit: 1, publishTimes: '', titlePrefix: '', cta: '', hashtags: '' });
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const saveAccount = async (account: ComedyAccount, patch: Record<string, unknown>) => {
    setBusy(true); setError(null);
    try {
      await api.comedy.updateAccount(channel.id, account.accountId, { ...account.settings, ...patch, enabled: account.enabled });
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const unassign = async (account: ComedyAccount) => {
    if (!window.confirm(`Odpiąć profil ${account.name}? Zaplanowane, niewysłane publikacje tego kanału zostaną anulowane.`)) return;
    setBusy(true);
    try { await api.comedy.unassignAccount(channel.id, account.accountId); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const options = useMemo(() => available.filter((account) => account.selectable && !assigned.some((entry) => entry.accountId === account.accountId)), [available, assigned]);
  const MAX_BULK = 20;
  const toggleSelection = (id: string) => setSelectedIds((current) => current.includes(id) ? current.filter((entry) => entry !== id) : current.length >= MAX_BULK ? current : [...current, id]);
  const addPlatform = (platform: string) => setSelectedIds((current) => [...new Set([...current, ...options.filter((account) => account.platform === platform).map((account) => account.accountId)])].slice(0, MAX_BULK));
  if (loading) return <Spinner label="Ładowanie profili social media…" />;

  return (
    <ComedySection title="Przypisane profile social media" description="Jeden profil może należeć do jednego kanału tematycznego. Publikacja zawsze trafia do dokładnie wskazanego konta.">
      {error ? <ErrorAlert error={error} /> : null}
      {assigned.length === 0 ? (
        <div className="alert alert--warning">Brak przypisanych profili. Kanał może produkować filmy, ale nie może publikować. Pamiętaj: nie zakładamy, że istniejące profile mają już przypisaną tematykę.</div>
      ) : (
        <div className="table-wrap" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead><tr><th>Platforma</th><th>Profil</th><th>Status</th><th>Limit/dzień</th><th>Godziny</th><th /></tr></thead>
            <tbody>
              {assigned.map((account) => (
                <tr key={account.accountId}>
                  <td>{COMEDY_PLATFORM_LABEL[account.platform] ?? account.platform}</td>
                  <td><strong>{account.name}</strong><div className="muted" style={{ fontSize: 11 }}>{account.username ?? account.accountId} · {account.publisherType}</div></td>
                  <td><ComedyStatus value={account.enabled ? account.status : 'DISABLED'} /></td>
                  <td><input style={{ width: 70 }} type="number" min={0} max={20} value={Number((account.settings as { dailyLimit?: number }).dailyLimit ?? 1)} onChange={(event) => void saveAccount(account, { dailyLimit: Number(event.target.value) })} /></td>
                  <td>{String((account.settings as { publishTimes?: string[] }).publishTimes?.join(', ') || 'kanał')}</td>
                  <td><Button size="sm" variant="danger" onClick={() => void unassign(account)} disabled={busy}>Odepnij</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <strong>Dodaj profile</strong>
          <span className="muted" style={{ fontSize: 12 }}>zaznaczono {selectedIds.length} z {options.length} dostępnych (max {MAX_BULK})</span>
          <div style={{ flex: 1 }} />
          <Button size="sm" onClick={() => setSelectedIds(options.map((account) => account.accountId).slice(0, 20))} disabled={!options.length}>Zaznacz wszystkie</Button>
          <Button size="sm" onClick={() => setSelectedIds([])} disabled={!selectedIds.length}>Wyczyść</Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {(['YOUTUBE', 'FACEBOOK', 'INSTAGRAM', 'TIKTOK'] as const).map((platform) => {
            const count = options.filter((account) => account.platform === platform).length;
            return <Button key={platform} size="sm" onClick={() => addPlatform(platform)} disabled={!count}>+ {COMEDY_PLATFORM_LABEL[platform]} ({count})</Button>;
          })}
        </div>
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8, marginBottom: 12 }}>
          {options.length === 0 ? (
            <div className="muted" style={{ padding: 8 }}>Wszystkie dostępne profile są już przypisane do tego kanału.</div>
          ) : options.map((account) => (
            <label key={account.accountId} className="inline" style={{ display: 'flex', gap: 8, padding: '7px 4px', borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={selectedIds.includes(account.accountId)} onChange={() => toggleSelection(account.accountId)} />
              <span className="badge badge--info">{COMEDY_PLATFORM_LABEL[account.platform] ?? account.platform}</span>
              <span style={{ flex: 1 }}><strong>{account.name}</strong>{account.username ? ` · ${account.username}` : ''}</span>
              <span className="muted" style={{ fontSize: 11 }}>{account.integrationStatus}</span>
            </label>
          ))}
        </div>
        <div className="form-row">
          <Field label="Wspólny limit publikacji / dzień" hint="Zostanie zastosowany do wszystkich zaznaczonych; potem możesz zmienić indywidualnie."><input type="number" min={0} max={20} value={settings.dailyLimit} onChange={(event) => setSettings((current) => ({ ...current, dailyLimit: Number(event.target.value) }))} /></Field>
          <Field label="Wspólne godziny profilu (opcjonalne)"><input value={settings.publishTimes} onChange={(event) => setSettings((current) => ({ ...current, publishTimes: event.target.value }))} placeholder="12:00, 18:00" /></Field>
        </div>
        <div className="form-row">
          <Field label="Prefiks tytułu"><input value={settings.titlePrefix} onChange={(event) => setSettings((current) => ({ ...current, titlePrefix: event.target.value }))} /></Field>
          <Field label="CTA"><input value={settings.cta} onChange={(event) => setSettings((current) => ({ ...current, cta: event.target.value }))} placeholder="Obejrzyj do końca…" /></Field>
        </div>
        <Field label="Dodatkowe hashtagi"><input value={settings.hashtags} onChange={(event) => setSettings((current) => ({ ...current, hashtags: event.target.value }))} /></Field>
        <div className="btn-row">
          <Button variant="primary" size="sm" onClick={() => void assign()} disabled={!selectedIds.length || busy}>{busy ? 'Przypisywanie…' : `Przypisz zaznaczone (${selectedIds.length})`}</Button>
          <Button size="sm" onClick={() => void load()} disabled={busy}>Odśwież statusy</Button>
        </div>
        {bulkMessage ? <div className="alert alert--success" style={{ marginTop: 12 }}>{bulkMessage}</div> : null}
        {available.some((account) => account.integrationStatus !== 'CONNECTED') && (
          <div className="alert alert--info" style={{ marginTop: 12 }}>
            {available.filter((account) => account.integrationStatus !== 'CONNECTED').slice(0, 3).map((account) => (
              <div key={account.accountId}>{COMEDY_PLATFORM_LABEL[account.platform] ?? account.platform} · {account.name}: {account.authorizationNote}</div>
            ))}
            <div style={{ marginTop: 6 }}>Autoryzację YouTube ustawisz w Social Accounts → „Połącz YouTube”. Facebook/Instagram/TikTok korzystają z profili Sociamonials.</div>
          </div>
        )}
      </div>
    </ComedySection>
  );
}
